import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Select,
  Button,
  Banner,
  Text,
  Box,
  Link,
  InlineStack, // Ensure InlineStack is imported
  SkeletonPage,
  SkeletonBodyText,
} from '@shopify/polaris';
import { AuthContext } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';

export default function IntegrateGa4Only() {
  const navigate = useNavigate();
  const { token, setToken } = useContext(AuthContext);

  const [loading, setLoading] = useState(true);
  const [propsList, setPropsList] = useState([]);
  const [propId, setPropId] = useState('');
  const [savedPropId, setSavedPropId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [success, setSuccess] = useState(false);

  // Handle OAuth redirect (capture JWT)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (t) {
      setToken(t);
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [setToken]);

  // Fetch saved propertyId (if any)
  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/integrations/property`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const json = await res.json();
        if (json.propertyId) {
          setSavedPropId(json.propertyId);
          setPropId(json.propertyId);
        }
      } catch (e) {
        // Handle error gracefully, maybe log it but don't set generalError
        console.error("Failed to fetch saved property ID:", e);
      }
    })();
  }, [token]);

  // Fetch available GA4 properties
  useEffect(() => {
    // Only fetch properties if a token exists
    if (!token) {
      setLoading(false); // If no token, we can't load properties, so stop loading state
      setPropsList([]); // Clear any previous properties if token is gone
      setPropId('');    // Clear selected property
      return;
    }
    (async () => {
      try {
        const res = await fetch(`${API}/integrations/ga4-properties`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
            const errorText = await res.text();
            throw new Error(`Failed to fetch GA4 properties: ${errorText}`);
        }
        const json = await res.json();
        setPropsList(json);
        // If there are properties and no propId is selected, or the current propId is not in the new list,
        // select the first one.
        if (json.length > 0 && (!propId || !json.some(p => p.measurementId === propId))) {
          setPropId(json[0].measurementId);
        } else if (json.length === 0) {
            setPropId(''); // Clear propId if no properties are found
        }
      } catch (e) {
        setGeneralError(e.message || 'Error loading GA4 properties.');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, propId]); // Added propId to the dependency array to resolve ESLint warning

  const handleConnect = async () => {
    setProcessing(true);
    setGeneralError('');
    setSuccess(false);
    try {
      const res = await fetch(`${API}/integrations/connect-ga4-only`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ propertyId: propId }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text);
      }
      setSuccess(true);
      setSavedPropId(propId);
    } catch (e) {
      setGeneralError(e.message);
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Page title="Connect GA4 Only">
        <SkeletonPage title="Connect Google Analytics">
          <SkeletonBodyText lines={4} />
        </SkeletonPage>
      </Page>
    );
  }

  return (
    <Page title="Google Analytics Integration" backAction={{ onAction: () => navigate(-1) }}>
      <Layout.Section>
        <Card sectioned>
          {/* Always display the "Connect Google Account" button at the top of the card */}
          <Box paddingBlockEnd="4">
            <Button
              primary
              onClick={() => {
                window.location.href = `${API}/auth/google?next=/integrateGa4Only`;
              }}
            >
              Connect Google Account
            </Button>
          </Box>

          {/* Always display the directions on how to find/create a GA4 property */}
          <Box paddingBlockStart="4" paddingBlockEnd="4">
            <Banner status="info">
              <Text as="p" fontWeight="bold">How to Find or Create a Google Analytics 4 Property</Text>
              <Text as="p">
                If you don't see your property in the dropdown below, or need to create a new one, follow these steps:
              </Text>
              <ol>
                <li>Go to the <Link external url="https://analytics.google.com/">Google Analytics website</Link>.</li>
                <li>Sign in with the Google account you wish to connect.</li>
                <li>In the Admin section (gear icon), click "Create Property".</li>
                <li>Follow the instructions to set up a new GA4 property for your website. Make sure to choose "Web" for the data stream.</li>
                <li>Once created, you will get a "Measurement ID" (e.g., `G-XXXXXXXXXX`).</li>
                <li>After creating the property, you may need to re-connect your Google account here (if prompted for permissions) and then refresh this page to see your new property.</li>
              </ol>
              <Text as="p">
                If you've just created a new property, it might take a few minutes for it to appear in the list.
              </Text>
            </Banner>
          </Box>

          {/* Display property selection and save options only if a token exists */}
          {token ? (
            <>
              <Text as="p" variant="bodyMd">
                Select and save your Google Analytics 4 property for your account.
              </Text>

              {propsList.length > 0 ? (
                <>
                  <Box paddingBlockStart="4">
                    <Select
                      label="GA4 Property (Measurement ID)"
                      options={propsList.map((p) => ({
                        label: `${p.displayName} (${p.measurementId})`,
                        value: p.measurementId,
                      }))}
                      onChange={setPropId}
                      value={propId}
                      helpText="Choose the GA4 property you want to connect to your account."
                    />
                  </Box>

                  <Box paddingBlockStart="4">
                    <InlineStack distribution="trailing">
                      <Button
                        primary
                        onClick={handleConnect}
                        loading={processing}
                        disabled={processing || !propId}
                      >
                        {processing ? 'Saving...' : 'Save GA4 Property'}
                      </Button>
                      {/* New "Go to Dashboard" button */}
                      <Button onClick={() => navigate('/Dashboard')}>
                        Go to Dashboard
                      </Button>
                    </InlineStack>
                  </Box>
                </>
              ) : (
                <Box paddingBlockStart="4">
                  <Banner status="info">
                    <Text as="p" fontWeight="bold">No Google Analytics 4 properties found for the connected account.</Text>
                    <Text as="p">
                      Please ensure your Google account has existing GA4 properties, or create one using the instructions above.
                    </Text>
                  </Banner>
                </Box>
              )}

              {success && (
                <Box paddingBlockStart="4">
                  <Banner status="success">
                    <Text as="p">✅ GA4 property saved: {savedPropId}</Text>
                  </Banner>
                </Box>
              )}

              {generalError && (
                <Box paddingBlockStart="4">
                  <Banner status="critical">
                    <Text as="p">{generalError}</Text>
                  </Banner> {/* Fixed: Added missing closing tag for Banner */}
                </Box>
              )}
            </>
          ) : (
            <Box paddingBlockStart="4">
                <Banner status="warning">
                    <Text as="p">
                        Please use the "Connect Google Account" button above to link your Google account and view your GA4 properties.
                    </Text>
                </Banner>
            </Box>
          )}
        </Card>
      </Layout.Section>
    </Page>
  );
}